/**
 * StarButton component for starring/unstarring leads
 * Shows filled star when starred, outline when not starred
 */

import React, { useState, useCallback } from 'react';
import { starService } from '../../services/starService';
import { useAuth } from '../../contexts/AuthContext';

interface StarButtonProps {
  leadId: string;
  isStarred: boolean;
  onStarChange?: (isStarred: boolean) => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

export const StarButton: React.FC<StarButtonProps> = ({
  leadId,
  isStarred,
  onStarChange,
  size = 'medium',
  disabled = false
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [localIsStarred, setLocalIsStarred] = useState(isStarred);

  // Size configurations
  const sizeConfig = {
    small: { size: 16, padding: '4px' },
    medium: { size: 20, padding: '6px' },
    large: { size: 24, padding: '8px' }
  };

  const config = sizeConfig[size];

  const handleStarToggle = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click events

    if (!user || disabled || isLoading) return;

    console.log(`🔄 StarButton: Toggle star for leadId=${leadId}, current state: localIsStarred=${localIsStarred}, prop isStarred=${isStarred}`);
    setIsLoading(true);

    try {
      if (localIsStarred) {
        console.log(`🗑️ StarButton: Attempting to unstar lead ${leadId} for user ${user.uid}`);
        await starService.unstarLead({
          leadId,
          userUid: user.uid
        });
        setLocalIsStarred(false);
        onStarChange?.(false);
        console.log(`✅ StarButton: Successfully unstarred lead ${leadId}`);
      } else {
        console.log(`⭐ StarButton: Attempting to star lead ${leadId} for user ${user.uid}`);
        const result = await starService.starLead({
          leadId,
          userUid: user.uid
        });

        // If we get a result (either new star or existing star), update UI
        if (result) {
          setLocalIsStarred(true);
          onStarChange?.(true);
          console.log(`✅ StarButton: Successfully starred lead ${leadId}`);
        }
      }
    } catch (error) {
      console.error(`❌ StarButton: Failed to toggle star for lead ${leadId}:`, error);

      // If the error is "Lead is already starred", we should sync the UI state
      if (error instanceof Error && error.message.includes('already starred')) {
        console.log(`⚠️ StarButton: Lead ${leadId} is already starred, syncing UI state...`);
        setLocalIsStarred(true);
        onStarChange?.(true);
      } else {
        // For other errors, revert optimistic update
        setLocalIsStarred(isStarred);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, disabled, isLoading, localIsStarred, leadId, onStarChange, isStarred]);

  // Update local state when prop changes
  React.useEffect(() => {
    if (localIsStarred !== isStarred) {
      console.log(`🔄 StarButton state sync: leadId=${leadId}, prop=${isStarred}, local=${localIsStarred}`);
      setLocalIsStarred(isStarred);
    }
  }, [isStarred, localIsStarred, leadId]);

  if (!user) return null;

  return (
    <button
      onClick={handleStarToggle}
      disabled={disabled || isLoading}
      style={{
        background: 'none',
        border: 'none',
        cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
        padding: config.padding,
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : isLoading ? 0.7 : 1,
        transform: isLoading ? 'scale(0.95)' : 'scale(1)',
      }}
      onMouseEnter={(e) => {
        if (!disabled && !isLoading) {
          e.currentTarget.style.backgroundColor = 'rgba(255, 193, 7, 0.1)';
          e.currentTarget.style.transform = 'scale(1.1)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !isLoading) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
      title={localIsStarred ? 'Remove from starred' : 'Add to starred'}
      aria-label={localIsStarred ? 'Remove from starred' : 'Add to starred'}
    >
      {localIsStarred ? (
        // Filled star
        <svg
          width={config.size}
          height={config.size}
          viewBox="0 0 24 24"
          fill="#ffc107"
          style={{
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
            transition: 'all 0.2s ease'
          }}
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ) : (
        // Outline star
        <svg
          width={config.size}
          height={config.size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6b7280"
          strokeWidth="2"
          style={{
            transition: 'all 0.2s ease'
          }}
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      )}
    </button>
  );
};